// Modules
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"

// Constants
import PATHS from "~constants/paths"
import SESSION_KEYS from "~constants/session"
import { ERROR_MESSAGES } from "~constants/errorMessages"

// Utils
import { setMemoryToken } from "~utils/auth-token"

// Context
import { useAuthContext } from "~context/authContext"

// Services
import UserService from "~services/UserService"
import AuthService from "~services/AuthService"

// Utils
import { removeStoredItem } from "~utils/store"

// Components
import Title from "~styled/title"
import { FormSubmit } from "~styled/form"
import PasswordWithConfirmation from "~components/PasswordWithConfirmation"
import { AuthForm, AuthError, AuthDescription } from "~styled/auth"

const NewPasswordForm = () => {
  const navigate = useNavigate()

  // Get from context
  const {
    loggedUser,
    getLoggedUserCompany,
    isLoading: authLoading,
  } = useAuthContext()

  // Don't render if still loading or user is not loaded yet
  if (authLoading || !loggedUser) {
    return null
  }

  // Set component state
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>(null!)
  const [passwordUpdated, setPasswordUpdated] = useState<boolean>(false)

  // Form definition
  const {
    formState: { errors },
    register,
    getValues,
    clearErrors,
    handleSubmit,
  } = useForm({
    defaultValues: { password: "", passwordConfirm: "" },
  })

  // Method to handle submit
  const onSubmit = async (data: {
    password: string
    passwordConfirm: string
  }) => {
    if (passwordUpdated) {
      await getLoggedUserCompany(loggedUser)
      removeStoredItem(SESSION_KEYS.IS_RESETTING_PASSWORD)
      navigate(PATHS.basePath)
      return
    }

    setIsLoading(true)

    try {
      const payload = { password: data.password }
      await UserService.patchNewPassword(payload, loggedUser.id)

      // The reset flow only holds a reset_password-scoped token in memory and
      // never established a SPA session. Now that the new password is set, get a
      // full-scope token via the client-only OAuth password grant and keep it in
      // memory so the app can navigate internally. This deliberately avoids the
      // SPA/BFF endpoints (spaLogin/spaRefresh) so the reset flow has no
      // server-session dependency.
      const response = await AuthService.postAuthenticate({
        username: loggedUser.userName,
        password: data.password,
      })
      setMemoryToken(response.access_token)

      setPasswordUpdated(true)
    } catch (error) {
      setErrorMessage(ERROR_MESSAGES.GENERIC)
    }

    setIsLoading(false)
  }

  return (
    <AuthForm onSubmit={handleSubmit(onSubmit)}>
      <Title>New Password</Title>

      <AuthDescription>
        {!passwordUpdated
          ? "Please enter your new password."
          : "Password successfully updated."}
      </AuthDescription>

      {!passwordUpdated && (
        <PasswordWithConfirmation
          errors={errors}
          register={register}
          onChange={() => clearErrors()}
          getValues={getValues}
        />
      )}

      <FormSubmit type="submit" disabled={isLoading}>
        {!passwordUpdated ? "Save" : "Done"}
      </FormSubmit>

      {!!errorMessage && <AuthError>{errorMessage}</AuthError>}
    </AuthForm>
  )
}

export default NewPasswordForm
